import { useState, useEffect } from 'react';
import { ModalPortal } from '@/components/ui/Modal/ModalPortal';
import { X, Building2, Loader2, Server, Globe, CheckCircle, XCircle, Database, Link } from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  description: string | null;
  maxUsers: number;
  maxChannels: number;
  priceMonthly: number;
  features: string[];
  integrations: string[];
  isActive: boolean;
}

interface ChannelOption {
  slug: string;
  displayName: string;
  type: string;
}

interface Tenant {
  id: string;
  slug: string;
  name: string;
  rut: string;
  isActive: boolean;
  trialEndsAt: string | null;
  planId: string | null;
  hostingType: 'shared' | 'dedicated';
  externalUrl: string | null;
  externalApiUrl: string | null;
  externalDbHost: string | null;
  externalDbPort: number | null;
  externalDbName: string | null;
  externalDbUser: string | null;
  externalDbPassword: string | null;
  channels?: string[];
  premiumFeatures?: string[];
}

interface EditTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tenant: Tenant;
}

export default function EditTenantModal({
  isOpen,
  onClose,
  onSuccess,
  tenant,
}: EditTenantModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    rut: '',
    isActive: true,
    trialDays: 0,
    planId: '',
    hostingType: 'shared' as 'shared' | 'dedicated',
    externalUrl: '',
    externalApiUrl: '',
    externalDbHost: '',
    externalDbPort: '5432',
    externalDbName: '',
    externalDbUser: '',
    externalDbPassword: '',
    channels: [] as string[],
    premiumFeatures: [] as string[],
  });
  const [plans, setPlans] = useState<Plan[]>([]);
  const [channelOptions, setChannelOptions] = useState<ChannelOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [connectionMessage, setConnectionMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchPlans();
      fetchChannels();
      setConnectionStatus('idle');
      setConnectionMessage('');
    }
  }, [isOpen]);

  const fetchPlans = async () => {
    setIsLoadingPlans(true);
    try {
      const res = await fetch('/api/super-admin/plans', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });
      const data = await res.json();
      setPlans(data.data || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const res = await fetch('/api/super-admin/channels-catalog', {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const json = await res.json();
      setChannelOptions(
        (json.data || [])
          .filter((c: any) => c.isActive !== false)
          .map((c: any) => ({
            slug: c.slug,
            displayName: c.displayName || c.display_name || c.slug,
            type: c.type,
          })),
      );
    } catch {
      setChannelOptions([]);
    }
  };

  useEffect(() => {
    if (tenant && isOpen) {
      const trialDays = tenant.trialEndsAt
        ? Math.max(
            0,
            Math.ceil(
              (new Date(tenant.trialEndsAt).getTime() - Date.now()) /
                (1000 * 60 * 60 * 24)
            )
          )
        : 0;

      setFormData({
        name: tenant.name,
        slug: tenant.slug,
        rut: tenant.rut,
        isActive: tenant.isActive,
        trialDays,
        planId: tenant.planId || '',
        hostingType: tenant.hostingType || 'shared',
        externalUrl: tenant.externalUrl || '',
        externalApiUrl: (tenant as any).externalApiUrl || '',
        externalDbHost: tenant.externalDbHost || '',
        externalDbPort: tenant.externalDbPort?.toString() || '5432',
        externalDbName: tenant.externalDbName || '',
        externalDbUser: tenant.externalDbUser || '',
        externalDbPassword: tenant.externalDbPassword || '',
        channels: tenant.channels || [],
        premiumFeatures: tenant.premiumFeatures || [],
      });
    }
  }, [tenant, isOpen]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? target.checked : value,
    }));
    setConnectionStatus('idle');
  };

  const toggleChannel = (slug: string) => {
    setFormData((prev) => ({
      ...prev,
      channels: prev.channels.includes(slug)
        ? prev.channels.filter((s) => s !== slug)
        : [...prev.channels, slug],
    }));
  };

  const togglePremiumFeature = (feature: string) => {
    setFormData((prev) => ({
      ...prev,
      premiumFeatures: prev.premiumFeatures.includes(feature)
        ? prev.premiumFeatures.filter((f) => f !== feature)
        : [...prev.premiumFeatures, feature],
    }));
  };

  const selectedPlan = plans.find((p) => p.id === formData.planId);
  const maxChannels = selectedPlan?.maxChannels ?? 0;

  const testConnection = async () => {
    setIsTestingConnection(true);
    setConnectionStatus('idle');
    setConnectionMessage('');

    try {
      const res = await fetch('/api/super-admin/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({
          host: formData.externalDbHost,
          port: parseInt(formData.externalDbPort),
          database: formData.externalDbName,
          user: formData.externalDbUser,
          password: formData.externalDbPassword,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setConnectionStatus('success');
        setConnectionMessage('Conexión exitosa');
      } else {
        setConnectionStatus('error');
        setConnectionMessage(data.message || 'Error de conexión');
      }
    } catch (err: any) {
      setConnectionStatus('error');
      setConnectionMessage(err.message || 'No se pudo conectar');
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (maxChannels > 0 && formData.channels.length > maxChannels) {
        throw new Error(`El plan permite máximo ${maxChannels} canales`);
      }

      const payload: any = {
        name: formData.name,
        slug: formData.slug,
        rut: formData.rut,
        isActive: formData.isActive,
        trialDays: formData.trialDays,
        planId: formData.planId || null,
        hostingType: formData.hostingType,
        channels: formData.channels,
        premiumFeatures: formData.premiumFeatures,
      };

      if (formData.hostingType === 'dedicated') {
        payload.externalUrl = formData.externalUrl || null;
        payload.externalApiUrl = formData.externalApiUrl || null;
        payload.externalDbHost = formData.externalDbHost || null;
        payload.externalDbPort = formData.externalDbPort ? parseInt(formData.externalDbPort) : null;
        payload.externalDbName = formData.externalDbName || null;
        payload.externalDbUser = formData.externalDbUser || null;
        payload.externalDbPassword = formData.externalDbPassword || null;
      } else {
        payload.externalUrl = null;
        payload.externalApiUrl = null;
        payload.externalDbHost = null;
        payload.externalDbPort = null;
        payload.externalDbName = null;
        payload.externalDbUser = null;
        payload.externalDbPassword = null;
      }

      const res = await fetch(`/api/super-admin/tenants/${tenant.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Error al actualizar tenant');
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalPortal>
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel max-w-2xl modal-panel-scroll p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-lyrax-primary/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-lyrax-primary" />
            </div>
            <h2 className="text-xl font-bold text-lyrax-text">Editar Tenant</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-lyrax-text-secondary hover:text-lyrax-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="label">
                Nombre de la empresa
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                className="input"
                placeholder="Acme Corp"
                required
              />
            </div>

            <div>
              <label htmlFor="rut" className="label">
                RUT
              </label>
              <input
                id="rut"
                name="rut"
                type="text"
                value={formData.rut}
                onChange={handleChange}
                className="input"
                placeholder="12.345.678-9"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="slug" className="label">
              Subdominio
            </label>
            <div className="flex items-center">
              <input
                id="slug"
                name="slug"
                type="text"
                value={formData.slug}
                onChange={handleChange}
                className="input rounded-r-none"
                placeholder="acme-corp"
                pattern="^[a-z0-9-]+$"
                required
              />
              <span className="px-3 py-2 bg-lyrax-surface-hover border border-l-0 border-lyrax-border rounded-r-lg text-lyrax-text-secondary text-sm">
                .lyrax.cl
              </span>
            </div>
          </div>

          <div>
            <label className="label">Tipo de Hosting</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, hostingType: 'shared' }));
                  setConnectionStatus('idle');
                }}
                className={`p-3 rounded-lg border transition-all ${
                  formData.hostingType === 'shared'
                    ? 'border-lyrax-primary bg-lyrax-primary/10'
                    : 'border-lyrax-border bg-lyrax-surface-hover hover:border-lyrax-primary/50'
                }`}
              >
                <Server className={`w-5 h-5 mx-auto mb-1 ${
                  formData.hostingType === 'shared' ? 'text-lyrax-primary' : 'text-lyrax-text-secondary'
                }`} />
                <p className={`text-sm font-medium ${
                  formData.hostingType === 'shared' ? 'text-lyrax-primary' : 'text-lyrax-text'
                }`}>
                  Compartido
                </p>
                <p className="text-xs text-lyrax-text-secondary mt-0.5">
                  Schema en servidor Lyrax
                </p>
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, hostingType: 'dedicated' }));
                }}
                className={`p-3 rounded-lg border transition-all ${
                  formData.hostingType === 'dedicated'
                    ? 'border-lyrax-primary bg-lyrax-primary/10'
                    : 'border-lyrax-border bg-lyrax-surface-hover hover:border-lyrax-primary/50'
                }`}
              >
                <Globe className={`w-5 h-5 mx-auto mb-1 ${
                  formData.hostingType === 'dedicated' ? 'text-lyrax-primary' : 'text-lyrax-text-secondary'
                }`} />
                <p className={`text-sm font-medium ${
                  formData.hostingType === 'dedicated' ? 'text-lyrax-primary' : 'text-lyrax-text'
                }`}>
                  Dedicado
                </p>
                <p className="text-xs text-lyrax-text-secondary mt-0.5">
                  Servidor propio
                </p>
              </button>
            </div>
          </div>

          {formData.hostingType === 'dedicated' && (
            <>
              <div>
                <label htmlFor="externalUrl" className="label">
                  URL del Frontend (app del cliente)
                </label>
                <input
                  id="externalUrl"
                  name="externalUrl"
                  type="url"
                  value={formData.externalUrl}
                  onChange={handleChange}
                  className="input"
                  placeholder="https://protectora.lyrax.cl"
                />
              </div>

              <div>
                <label htmlFor="externalApiUrl" className="label">
                  URL de la API del cliente
                </label>
                <input
                  id="externalApiUrl"
                  name="externalApiUrl"
                  type="url"
                  value={formData.externalApiUrl}
                  onChange={handleChange}
                  className="input"
                  placeholder="https://api.protectora.lyrax.cl"
                />
                <p className="mt-1 text-xs text-lyrax-text-secondary">
                  API dedicada del cliente (deployed en su propio servidor)
                </p>
              </div>

              <div className="border-t border-lyrax-border pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Database className="w-4 h-4 text-lyrax-text-secondary" />
                  <span className="text-sm font-medium text-lyrax-text">Configuración de Base de Datos</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="externalDbHost" className="label">
                      Host
                    </label>
                    <input
                      id="externalDbHost"
                      name="externalDbHost"
                      type="text"
                      value={formData.externalDbHost}
                      onChange={handleChange}
                      className="input"
                      placeholder="db.cliente.cl"
                    />
                  </div>
                  <div>
                    <label htmlFor="externalDbPort" className="label">
                      Puerto
                    </label>
                    <input
                      id="externalDbPort"
                      name="externalDbPort"
                      type="text"
                      value={formData.externalDbPort}
                      onChange={handleChange}
                      className="input"
                      placeholder="5432"
                    />
                  </div>
                  <div>
                    <label htmlFor="externalDbName" className="label">
                      Nombre BD
                    </label>
                    <input
                      id="externalDbName"
                      name="externalDbName"
                      type="text"
                      value={formData.externalDbName}
                      onChange={handleChange}
                      className="input"
                      placeholder="lyrax_db"
                    />
                  </div>
                  <div>
                    <label htmlFor="externalDbUser" className="label">
                      Usuario
                    </label>
                    <input
                      id="externalDbUser"
                      name="externalDbUser"
                      type="text"
                      value={formData.externalDbUser}
                      onChange={handleChange}
                      className="input"
                      placeholder="postgres"
                    />
                  </div>
                  <div className="col-span-2">
                    <label htmlFor="externalDbPassword" className="label">
                      Contraseña
                    </label>
                    <input
                      id="externalDbPassword"
                      name="externalDbPassword"
                      type="password"
                      value={formData.externalDbPassword}
                      onChange={handleChange}
                      className="input"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-3">
                  <button
                    type="button"
                    onClick={testConnection}
                    disabled={isTestingConnection || !formData.externalDbHost || !formData.externalDbName}
                    className="btn-secondary text-sm flex items-center gap-2"
                  >
                    {isTestingConnection ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Link className="w-4 h-4" />
                    )}
                    {isTestingConnection ? 'Probando...' : 'Probar conexión'}
                  </button>
                  {connectionStatus === 'success' && (
                    <span className="flex items-center gap-1 text-sm text-green-500">
                      <CheckCircle className="w-4 h-4" />
                      {connectionMessage}
                    </span>
                  )}
                  {connectionStatus === 'error' && (
                    <span className="flex items-center gap-1 text-sm text-red-500">
                      <XCircle className="w-4 h-4" />
                      {connectionMessage}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}

          <div>
            <label htmlFor="planId" className="label">
              Plan
            </label>
            <select
              id="planId"
              name="planId"
              value={formData.planId}
              onChange={handleChange}
              className="input"
              disabled={isLoadingPlans}
            >
              <option value="">Sin plan asignado</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} - ${plan.priceMonthly?.toLocaleString('es-CL') || 0}/mes
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="label">Canales habilitados</p>
            <p className="mb-2 text-xs text-lyrax-text-secondary">
              Define qué integraciones puede configurar este tenant
              {maxChannels > 0 ? ` (máx. ${maxChannels} por plan)` : ''}
            </p>
            <div className="flex flex-wrap gap-2">
              {channelOptions.map((channel) => (
                <button
                  key={channel.slug}
                  type="button"
                  onClick={() => toggleChannel(channel.slug)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    formData.channels.includes(channel.slug)
                      ? 'bg-lyrax-primary/15 text-emerald-900 border-lyrax-primary dark:text-lyrax-primary'
                      : 'bg-lyrax-bg text-lyrax-text border-lyrax-border hover:border-lyrax-primary/50'
                  }`}
                >
                  {channel.displayName}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="label">Módulos Pro</p>
            <p className="mb-2 text-xs text-lyrax-text-secondary">
              Licencias de módulos premium independientes de los canales
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => togglePremiumFeature('donaciones')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  formData.premiumFeatures.includes('donaciones')
                    ? 'bg-orange-500/15 text-orange-800 border-orange-500 dark:text-orange-300'
                    : 'bg-lyrax-bg text-lyrax-text border-lyrax-border hover:border-orange-400/50'
                }`}
              >
                Donaciones
              </button>
              <button
                type="button"
                onClick={() => togglePremiumFeature('chatbot')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  formData.premiumFeatures.includes('chatbot')
                    ? 'bg-orange-500/15 text-orange-800 border-orange-500 dark:text-orange-300'
                    : 'bg-lyrax-bg text-lyrax-text border-lyrax-border hover:border-orange-400/50'
                }`}
              >
                Asistente IA
              </button>
              <button
                type="button"
                onClick={() => togglePremiumFeature('email')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  formData.premiumFeatures.includes('email')
                    ? 'bg-orange-500/15 text-orange-800 border-orange-500 dark:text-orange-300'
                    : 'bg-lyrax-bg text-lyrax-text border-lyrax-border hover:border-orange-400/50'
                }`}
              >
                Email
              </button>
            </div>
          </div>

          <div>
            <p className="label">Productos</p>
            <p className="mb-2 text-xs text-lyrax-text-secondary">
              Productos del ecosistema, independientes de Lyrax Pro
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => togglePremiumFeature('erp')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  formData.premiumFeatures.includes('erp')
                    ? 'bg-slate-700/15 text-slate-800 border-slate-600 dark:text-slate-300'
                    : 'bg-lyrax-bg text-lyrax-text border-lyrax-border hover:border-slate-400/50'
                }`}
              >
                ERP
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="trialDays" className="label">
              Días de Trial
            </label>
            <input
              id="trialDays"
              name="trialDays"
              type="number"
              min="0"
              value={formData.trialDays}
              onChange={handleChange}
              className="input"
              placeholder="0"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              id="isActive"
              name="isActive"
              type="checkbox"
              checked={formData.isActive}
              onChange={handleChange}
              className="w-4 h-4 rounded border-lyrax-border text-lyrax-primary focus:ring-lyrax-primary"
            />
            <label htmlFor="isActive" className="text-sm text-lyrax-text">
              Tenant activo
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={isSubmitting}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </ModalPortal>
  );
}
